<template>
    <ModalLayout
        :visible="visible"
        @destroy="close"
        @success="submitForm"
        :success-loading="loading"
        :tabs="tabItems"
        :success-label="$t('design_mail_branding.editor.save_button')"
        :tabActive="activeTab"
        @tabSelected="(tab) => activeTab = tab"
    >
        <template v-slot:title>{{ $t('design_mail_branding.editor.modal_title') }}</template>

        <template v-slot:settingArea>
            <p>
                {{ $t('design_mail_branding.editor.modal_description') }}
            </p>

            <g-form :form="form" class="form-container" ref="formRef" :action="formEndpoint" :method="formMethod"
                    @loading-state="setLoadingState" @fail="setActiveStepForErrors" @success="formSuccess">
                <accordeon>
                    <accordeon-item
                        :title="$t('design_mail_branding.editor.design_section')"
                        :top-title="$t('accordeon.step', [1])"
                        :active="activeStep === 1"
                        @select="selectedStep(1)"
                        :has-errors="stepErrors[1]"
                        :has-warnings="false"
                    >
                        <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                            <div>
                                <div>
                                    <label class="group-label">
                                        {{ $t('design_mail_branding.editor.design_group') }}
                                    </label>

                                    <g-input name="name" :label="$t('design_mail_branding.editor.name_label')"/>

                                    <label class="group-label">
                                        {{ $t('design_mail_branding.editor.font_group') }}
                                    </label>

                                    <g-select :options="fontFamilyOptions" name="font_family"/>

                                    <div class="field-body">
                                        <div class="left-column">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.logo') }}
                                            </label>

                                            <div v-if="logoPreview === null">
                                                <g-file
                                                    name="logo"
                                                    :label="$t('design_mail_branding.editor.logo_label')"
                                                    accept="image/png"
                                                    :match-inputs="true"
                                                    @change="setPreview($event, 'icon')"
                                                />
                                            </div>

                                            <div v-else class="image-preview">
                                                <div class="preview">
                                                    <img :src="logoPreview" alt="Icon preview" class="icon">
                                                </div>
                                                <div class="remove" @click="deleteLogo">
                                                    {{ $t('design_mail_branding.editor.remove_logo') }}
                                                </div>
                                            </div>
                                        </div>

                                        <div class="right-column">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.logo_alignment') }}
                                            </label>
                                            <div class="select-wrapper">
                                                <g-select :options="logoAlignmentOptions" name="logo_alignment"/>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div class="field-body">
                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.text_label') }}
                                            </label>

                                            <g-colorpicker ref="logoPalettePicker" name="content_foreground_color" :suggestions="colorSuggestions"/>
                                        </div>

                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.hyperlink_label') }}
                                            </label>

                                            <g-colorpicker name="content_hyperlink_color" :suggestions="colorSuggestions"/>
                                        </div>
                                    </div>

                                    <div class="field-body">
                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.email_background_label') }}
                                            </label>

                                            <g-colorpicker name="background_color" :suggestions="colorSuggestions"/>
                                        </div>

                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.content_background_label') }}
                                            </label>

                                            <g-colorpicker name="content_background_color" :suggestions="colorSuggestions"/>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <g-button @click.prevent="activeStep++" :is-rounded="false" class="is-pulled-right">
                                    {{ $t('design_mail_branding.editor.next_step') }}
                                </g-button>
                            </div>
                        </div>
                    </accordeon-item>

                    <accordeon-item
                        :title="$t('design_mail_branding.editor.elements_section')"
                        :top-title="$t('accordeon.step', [2])"
                        :active="activeStep === 2"
                        @select="selectedStep(2)"
                        :has-errors="stepErrors[2]"
                        :has-warnings="contrastRatioNotifications.length >= 1"
                    >
                        <div class="is-flex is-flex-direction-column is-justify-content-space-between form-min-height">
                            <div>
                                <div>
                                    <div class="field-body">
                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.table_background_label') }}
                                            </label>

                                            <g-colorpicker name="table_background_color" :suggestions="colorSuggestions"/>
                                        </div>

                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.table_text_label') }}
                                            </label>

                                            <g-colorpicker name="table_foreground_color" :suggestions="colorSuggestions"/>
                                        </div>
                                    </div>

                                    <div class="field-body">
                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.table_lines_label') }}
                                            </label>

                                            <g-colorpicker name="table_border_color" :suggestions="colorSuggestions"/>
                                        </div>

                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.divider_lines_label') }}
                                            </label>

                                            <g-colorpicker name="divider_color" :suggestions="colorSuggestions"/>
                                        </div>
                                    </div>

                                    <div class="field-body">
                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.button_background_label') }}
                                            </label>

                                            <g-colorpicker name="button_background_color" :suggestions="colorSuggestions"/>
                                        </div>

                                        <div class="">
                                            <label class="group-label">
                                                {{ $t('design_mail_branding.editor.button_text_label') }}
                                            </label>

                                            <g-colorpicker name="button_foreground_color" :suggestions="colorSuggestions"/>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div class="field-body">
                                        <label class="group-label">
                                            {{ $t('design_mail_branding.editor.button_design_group') }}
                                        </label>
                                    </div>

                                    <div class="field-body">
                                        <g-input type="number" min="0" max="100" icon="align_space_between" :label="$t('design_mail_branding.editor.vertical_space_label')" name="button_vertical_padding"/>
                                        <g-input type="number" min="0" max="100" icon="align_justify_space_between" :label="$t('design_mail_branding.editor.horizontal_space_label')" name="button_horizontal_padding"/>
                                        <g-input type="number" min="0" max="100" icon="rounded_corner" :label="$t('design_mail_branding.editor.rounding_label')" name="button_border_radius"/>
                                    </div>

                                    <template v-if="contrastRatioNotifications.length >= 1">
                                        <div class="field-body" v-for="(set, index) in contrastRatioNotifications" :key="index">
                                            <div class="notification is-warning mt-1 mb-1">
                                                <span v-html="$t('design_mail_branding.editor.contrast_warning', {
                                                    field1: $t(`design_mail_branding.editor.validation.${set[0]}`),
                                                    field2: $t(`design_mail_branding.editor.validation.${set[1]}`),
                                                })"/>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </accordeon-item>
                </accordeon>
            </g-form>
        </template>

        <template v-slot:previewArea>
            <tabbed-view @tabsInitialized="(tabs) => tabItems = tabs" ref="tabbedView">
                <tabbed-view-item
                    :title="$t('design_mail_branding.editor.preview_tab')"
                    :active="activeTab === 0"
                >
                    <mail-branding-preview :properties="form">
                        <template v-slot:header v-if="logoPreview !== null">
                            <div :style="{ textAlign: form.logo_alignment }">
                                <img alt="Company logo" :src="logoPreview" style="max-width: 128px; max-height: 74px;"/>
                            </div>
                        </template>
                    </mail-branding-preview>
                </tabbed-view-item>
            </tabbed-view>
        </template>
    </ModalLayout>
</template>

<script>
import ModalLayout from "../../ModalLayout.vue";
import Accordeon from "../../Accordeon.vue";
import AccordeonItem from "../../AccordeonItem.vue";
import TabbedView from "../../TabbedView.vue";
import TabbedViewItem from "../../TabbedViewItem.vue";
import TagsInput from "@johmun/vue-tags-input";
import LineTable from "../../LineTable.vue";
import GiftcardPreview from "../../../GiftcardPreview.vue";
import MailBrandingPreview from "./Preview.vue";
import EmailViewItem from "../DigitalOrders/EmailViewItem.vue";

export default {
    name: "MailTemplateEditor",
    components: {
        EmailViewItem,
        GiftcardPreview,
        LineTable,
        TagsInput,
        TabbedViewItem,
        TabbedView,
        AccordeonItem,
        Accordeon,
        ModalLayout,
        MailBrandingPreview,
    },

    props: {
        template: {
            type: Object,
            default: null,
        },
    },

    created() {
        if (this.template === null) {
            return;
        }

        if (this.template.logo) {
            this.logoPreview = this.template.logo;
        }

        this.form.id                        = this.template.id                        || this.form.id
        this.form.name                      = this.template.name                      || this.form.name
        this.form.font_family               = this.template.font_family               || this.form.font_family
        this.form.logo                      = null
        this.form.logo_alignment            = this.template.logo_alignment            || this.form.logo_alignment
        this.form.background_color          = this.template.background_color          || this.form.background_color
        this.form.content_foreground_color  = this.template.content_foreground_color  || this.form.content_foreground_color
        this.form.content_hyperlink_color   = this.template.content_hyperlink_color   || this.form.content_hyperlink_color
        this.form.content_background_color  = this.template.content_background_color  || this.form.content_background_color
        this.form.table_background_color    = this.template.table_background_color    || this.form.table_background_color
        this.form.table_foreground_color    = this.template.table_foreground_color    || this.form.table_foreground_color
        this.form.table_border_color        = this.template.table_border_color        || this.form.table_border_color
        this.form.divider_color             = this.template.divider_color             || this.form.divider_color
        this.form.button_background_color   = this.template.button_background_color   || this.form.button_background_color
        this.form.button_foreground_color   = this.template.button_foreground_color   || this.form.button_foreground_color
        this.form.button_vertical_padding   = this.template.button_vertical_padding   || this.form.button_vertical_padding
        this.form.button_horizontal_padding = this.template.button_horizontal_padding || this.form.button_horizontal_padding
        this.form.button_border_radius      = this.template.button_border_radius      || this.form.button_border_radius
    },

    computed: {
        formMethod() {
            return this.template?.id ? 'put' : 'post';
        },

        formEndpoint() {
            return this.template?.id ? `/api/mail-templates/${this.template?.id}` : '/api/mail-templates';
        },

        contrastRatioNotifications() {
            // The sets we want to check (formatted like name: [field, field])
            const sets = {
                // Button text must be readable against button background for call-to-action accessibility
                'button_text_button_background': ['button_foreground_color', 'button_background_color'],

                // Main body text must be readable against content background - most critical for email readability
                'content_text_content_background': ['content_foreground_color', 'content_background_color'],

                // Hyperlinks must be distinguishable and readable against content background for navigation
                'hyperlink_content_background': ['content_hyperlink_color', 'content_background_color'],

                // Regular text within tables must be readable against table background
                'table_text_content_background': ['table_foreground_color', 'table_background_color'],
            }

            // Convert hex to RGB (required to calculate contrast)
            const hexToRgb = (hex) => {
                if (!hex || !hex.startsWith('#')) {
                    return null;
                }

                const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

                return result ? {
                    r: parseInt(result[1], 16),
                    g: parseInt(result[2], 16),
                    b: parseInt(result[3], 16)
                } : null;
            }

            // Calculate relative luminance
            const getLuminance = (r, g, b) => {
                const [rs, gs, bs] = [r, g, b].map(c => {
                    c = c / 255;
                    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
                });

                return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
            }

            // Calculate contrast ratio
            const getContrastRatio = (color1, color2) => {
                const rgb1 = hexToRgb(color1)
                const rgb2 = hexToRgb(color2)

                if (!rgb1 || !rgb2) {
                    return 0
                }

                const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b)
                const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b)

                const brightest = Math.max(lum1, lum2)
                const darkest = Math.min(lum1, lum2)

                return (brightest + 0.05) / (darkest + 0.05)
            }

            const failingSets = []

            // Manually check the white background every logo has, against the content_background_color
            const contentBackgroundColor = this.form.content_background_color
            const logoBackgroundColor = '#ffffff'

            let contrastRatio = getContrastRatio(contentBackgroundColor, logoBackgroundColor)

            // In contrast (ba dum tss) to the checks below, we want a different ratio for logo's. This is because
            // we want the white background that logo's have to blend in with the content.
            if (contrastRatio > 2.0) {
                failingSets.push(['content_background_color', 'logo_background_color'])
            }

            // Calculate contrast ratios for each set using WCAG AA. (at least 4.5:1)
            for (const [setName, [foregroundField, backgroundField]] of Object.entries(sets)) {
                const foregroundColor = this.form[foregroundField]
                const backgroundColor = this.form[backgroundField]

                // Exclude the default button background color from checks
                if (backgroundColor.toLowerCase() === '#3dc173') {
                    continue
                }

                if (foregroundColor && backgroundColor) {
                    const contrastRatio = getContrastRatio(foregroundColor, backgroundColor)

                    if (contrastRatio < 4.5) {
                        failingSets.push([foregroundField, backgroundField])
                    }
                }
            }

            return failingSets
        }
    },

    data() {
        return {
            loading: false,
            activeStep: 1,
            activeTab: 0,
            tabItems: [],
            fieldStepMap: {
                1: [
                    'name',
                    'font_family',
                    'background_color',
                    'content_foreground_color',
                    'content_hyperlink_color',
                    'content_background_color',
                ],
                2: [
                    'table_background_color',
                    'table_foreground_color',
                    'table_border_color',
                    'divider_color',
                    'button_background_color',
                    'button_foreground_color',
                    'button_vertical_padding',
                    'button_horizontal_padding',
                    'button_border_radius',
                ],
            },
            stepErrors: {
                1: false,
                2: false,
            },
            form: new Form({
                id: null,
                name: '',
                font_family: 'helvetica',
                background_color: '#eceff0',
                content_foreground_color: '#6a7272',
                content_hyperlink_color: '#d40072',
                content_background_color: '#ffffff',
                table_background_color: '#ffffff',
                table_foreground_color: '#6a7272',
                table_border_color: '#efefef',
                divider_color: '#e9ecef',
                button_background_color: '#3dc173',
                button_foreground_color: '#ffffff',
                logo: null,
                delete_logo: false,
                logo_alignment: 'right',
                button_vertical_padding: 8,
                button_horizontal_padding: 18,
                button_border_radius: 22,
            }),
            logoPreview: null,
            logoAlignmentOptions: [
                {
                    value: 'left',
                    name: this.$t('design_mail_branding.editor.logo_alignment_left')
                },
                {
                    value: 'center',
                    name: this.$t('design_mail_branding.editor.logo_alignment_center')
                },
                {
                    value: 'right',
                    name: this.$t('design_mail_branding.editor.logo_alignment_right')
                }
            ],
            fontFamilyOptions: [
                { value: 'arial', name: 'Arial' },
                { value: 'helvetica', name: 'Helvetica' },
                { value: 'verdana', name: 'Verdana' },
                { value: 'tahoma', name: 'Tahoma' },
                { value: 'trebuchet_ms', name: 'Trebuchet MS' },
                { value: 'times_new_roman', name: 'Times New Roman' },
                { value: 'georgia', name: 'Georgia' },
                { value: 'garamond', name: 'Garamond' },
                { value: 'courier_new', name: 'Courier New' },
                { value: 'brush_script_mt', name: 'Brush Script MT' }
            ],
            colorSuggestions: ['#58bc58', '#a3dba3', '#070e07', '#7e7e7e', '#bcbcbc', '#444444'],
        }
    },

    mounted() {
        // Initialize color suggestions from the active company's logo if available
        this.updateLogoPalette()

        if (this.form.name === '') {
            this.form.name = this.$t('design_mail_branding.editor.default_template_name', {
                company: this.$store.getters['user/activeCompany'].name
            })
        }
    },

    methods: {
        deleteLogo() {
            this.form.delete_logo = true
            this.logoPreview = null
            this.form.logo = null
        },

        setPreview(event) {
            // Image preview (whilst it's still in-memory / local)
            const file = event.target.files[0]
            this.logoPreview = URL.createObjectURL(file)
            this.form.delete_logo = false

            this.updateLogoPalette()
        },

        updateLogoPalette() {
            const logo = this.logoPreview

            // Extract colors from the logo, so we can use them as suggestions
            if (logo && this.$refs.logoPalettePicker && typeof this.$refs.logoPalettePicker.setPalette === 'function') {
                try {
                    this.$refs.logoPalettePicker.setPalette(logo).then(palette => {
                        if (Array.isArray(palette) && palette.length > 0) {
                            this.colorSuggestions = palette
                        }
                    })
                } catch (e) {
                    console.log(e)
                }
            }
        },

        close() {
            this.$emit('destroy');
        },

        selectedStep(step) {
            if(this.activeStep === step) {
                step = step + 1;
            }

            if(step >= 3) {
                step = 1;
            }

            this.activeStep = step;
        },

        formSuccess() {
            this.$emit('template-saved')
            this.close()
        },

        setLoadingState(state) {
            this.loading = state;
        },

        setActiveStepForErrors(data) {
            // If data object does not contain an errors object, return
            if (!data.errors) {
                return;
            }

            let formErrorFields = [];

            // Loop through the errors object, it has the format of {field: [errors]}
            for (const field in data.errors) {
                // If the field is not in the form object, continue
                if (!this.form.hasOwnProperty(field)) {
                    continue;
                }

                // Add the field to the formErrorFields array
                formErrorFields.push(field);
            }

            // Reset step errors to prevent displaying old errors
            this.stepErrors = {
                1: false,
                2: false,
            };

            let stepsWithErrors = [];

            // Loop through the fieldStepMap object
            for (const step in this.fieldStepMap) {
                // Loop through the fields in the fieldStepMap object
                for (const field of this.fieldStepMap[step]) {
                    // If the field is in the formErrorFields array, add the step to the stepsWithErrors array
                    if (formErrorFields.includes(field)) {
                        this.stepErrors[parseInt(step)] = true;
                        stepsWithErrors.push(parseInt(step));
                        break;
                    }
                }
            }

            // If the stepsWithErrors array is not empty, set the activeStep to the lowest step
            if (stepsWithErrors.length > 0) {
                this.activeStep = Math.min(...stepsWithErrors);
            }

            // Handle address-specific errors (existing logic)
            for (const field in data.errors) {
                if (field.startsWith('addresses')) {
                    const index = parseInt(field.match(/\d+/)[0]);
                    this.form.addresses[index].hasError = true;
                }
            }
        },

        submitForm() {
            this.$refs.formRef.submit()
        },
    }
}
</script>

<style scoped lang="scss">
::v-deep input[type="number"]::-webkit-outer-spin-button,
::v-deep input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

::v-deep input[type="number"] {
    -moz-appearance: textfield;
}

.gifty-color-picker {
    background-color: white;
    padding: 7px;
    border-radius: 6px;
    border: 1px solid rgb(239, 239, 239);
}

.form-min-height {
    min-height: 100%;
}

.form-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
}

ul.recipients {
    list-style: none;
    margin: 0;

    li {
        padding: 0.5rem 1rem 0.6rem;
        background-color: #f7f9fb;
        border-bottom: 0.063rem solid #efefef;
    }
}

input.disabled {
    opacity: 0.5;
}

.preview-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 1rem 0;
    max-width: 65%;
    align-self: center;

    @media (min-height: 850px) {
        max-width: 75%;
        margin-bottom: 2rem;
    }

    .controls {
        display: flex;
        justify-content: space-between;

        a {
            color: #1F1F1F;
            text-decoration: underline;

            &.is-active {
                cursor: default;
                font-weight: bold;
                text-decoration: none;
            }
        }
    }

    ::v-deep {
        canvas {
            max-width: 100% !important;
            height: auto !important;
            box-shadow: 0 0.688rem 3.375rem 0 rgba(0, 0, 0, 0.10);
            border-radius: 0.188rem;
            background: #FFFFFF;
        }
    }
}

.image-preview {
    width: 100%;
    height: auto;
    display: flex;
    margin-bottom: 1rem;
    align-items: center;

    .preview {
        border-radius: 6px;
        background: #FFFFFF;
        border: 1px solid #f0efef;
        text-align: center;
        padding: 0.65rem;
        font-size: 0;

        img.icon {
            min-width: 1.5rem;
            max-width: 4.5rem;
            height: 1.5rem;
        }
    }

    .remove {
        position: relative;
        top: -0.1rem;
        margin-left: 0.94rem;
        cursor: pointer;
        text-decoration: underline;
        color: #6A7272;
        font-size: 1rem;
    }
}
</style>
